import { Box } from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';

import {
    CustomEventWrapper,
    CustomMonthHeader,
    DayMarkerContext,
    DisciplineColorContext,
} from './components';
import { getDayDateRange } from './createVisitEvent';
import {
    CalendarBar,
    CalendarDayMarker,
    CalendarEvent,
    CalendarTile,
    EngineCalendarEvent,
    MAX_WINDOW_EVENTS,
} from './types';
import { determineVisibleEvents, getMonthHeight, groupTilesByDay } from './utils';

export const CALENDAR_HEIGHT = 500;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Shared month-grid engine.
export const MonthEventsCalendar = ({
    events,
    dayMarkers = [],
    months,
    disciplineColorMap,
    scrollToMonth,
}: {
    events: CalendarEvent[];
    // Single-day header badges (e.g. span boundaries). Rendered in the month day-number
    // header, not as RBC events.
    dayMarkers?: CalendarDayMarker[];
    months: Date[];
    disciplineColorMap: Map<string, string>;
    // Optional month to auto-scroll into view once rendered (e.g. an episode's start month).
    scrollToMonth?: Date | null;
}) => {
    const dateLocalizer = useMemo(() => momentLocalizer(moment), []);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Split into per-day tiles (with "See All" overflow once a day exceeds MAX_WINDOW_EVENTS)
    // and all-day bars, then hand the combined set to RBC.
    const calendarEvents = useMemo(() => {
        const tiles = events.filter((e): e is CalendarTile => e.kind === 'tile');
        const bars = events.filter((e): e is CalendarBar => e.kind === 'bar');

        const tilesByDay = groupTilesByDay(tiles);
        const result: EngineCalendarEvent[] = [];

        tilesByDay.forEach((dayTiles, dayKey) => {
            const { visible, hidden, needsOverflow } = determineVisibleEvents(dayTiles);

            result.push(...visible.map((tile) => ({ ...tile, allDay: false as const })));

            // Add overflow link if needed (positioned after visit events)
            // Only show overflow if there are exactly MAX_WINDOW_EVENTS visible events and there are hidden events to show
            const shouldShowOverflow =
                needsOverflow && visible.length === MAX_WINDOW_EVENTS && hidden.length > 0;

            if (shouldShowOverflow) {
                // Get the day's date range to ensure overflow stays within the same day
                const { start: dayStart, end: dayEnd } = getDayDateRange(dayKey);
                const dayStartTime = dayStart.getTime();
                const dayEndTime = dayEnd.getTime();

                // Calculate the overflow link start time to ensure it appears after all visible events
                // Use the maximum end time of all visible events to ensure proper ordering
                // Since react-big-calendar sorts by start time, we need the overflow to start after all visible events end
                const maxEndTime = Math.max(...visible.map((e) => e.end.getTime()));
                // Also check max start time in case events have different start times
                const maxStartTime = Math.max(...visible.map((e) => e.start.getTime()));
                // Use the latter of the two to ensure overflow appears after all events
                // Add a small buffer (1 minute) to ensure it appears after, but cap it to stay within the same day
                const calculatedStartTime = Math.max(maxEndTime, maxStartTime) + 60000; // Add 1 minute
                // Ensure the overflow start time is within the same day (at least 1 minute before day end)
                const safeEndTime = dayEndTime - 60000; // 1 minute before day end
                const overflowStartTime = Math.min(
                    Math.max(calculatedStartTime, dayStartTime),
                    safeEndTime,
                );

                result.push({
                    kind: 'overflow',
                    start: new Date(overflowStartTime),
                    end: dayEnd,
                    title: 'See All',
                    uuid: `overflow-${dayKey}`,
                    dayEvents: dayTiles,
                    hiddenCount: hidden.length,
                    allDay: false,
                });
            }
        });

        // Bars span the all-day row; give them the title/uuid RBC needs.
        bars.forEach((bar) => {
            result.push({ ...bar, allDay: true, title: bar.label, uuid: bar.id });
        });

        return result;
    }, [events]);

    const dayMarkersByDay = useMemo(() => {
        const map = new Map<string, CalendarDayMarker[]>();
        dayMarkers.forEach((marker) => {
            const key = moment(marker.date).format('YYYY-MM-DD');
            const existing = map.get(key) ?? [];
            map.set(key, [...existing, marker]);
        });
        return map;
    }, [dayMarkers]);

    // Stable identity so RBC doesn't re-mount the custom renderers each render.
    const calendarComponents = useMemo(
        () => ({ eventWrapper: CustomEventWrapper, month: { dateHeader: CustomMonthHeader } }),
        [],
    );

    // Auto-scroll to the requested month once rendered.
    useEffect(() => {
        if (!scrollToMonth || !scrollContainerRef.current) {
            return;
        }
        const container = scrollContainerRef.current;
        const targetMonthStr = moment(scrollToMonth).format('YYYY-MM');
        const targetEl = container.querySelector<HTMLElement>(`[data-month="${targetMonthStr}"]`);
        if (!targetEl) {
            return;
        }
        container.scrollTop +=
            targetEl.getBoundingClientRect().top - container.getBoundingClientRect().top;
    }, [scrollToMonth, months]);

    return (
        <DisciplineColorContext.Provider value={disciplineColorMap}>
            <DayMarkerContext.Provider value={dayMarkersByDay}>
                <Box
                    ref={scrollContainerRef}
                    height={`${CALENDAR_HEIGHT}px`}
                    overflow={'scroll'}
                    flex={1}
                    sx={{
                        '& .rbc-month-view': {
                            border: 'none',
                            borderBottom: '1px solid #e5e7eb',
                        },
                        '& .rbc-month-row': {
                            borderLeft: '1px solid #e5e7eb',
                            borderRight: '1px solid #e5e7eb',
                            position: 'relative',
                            paddingBottom: '30px',
                        },
                        '& .rbc-month-row + .rbc-month-row': {
                            borderTopColor: '#e5e7eb',
                        },
                        '& .rbc-day-bg': {
                            borderColor: '#e5e7eb',
                            background: '#ffffff',
                            position: 'relative',
                        },
                        '& .rbc-header': {
                            color: '#364153',
                            fontWeight: 400,
                            textTransform: 'uppercase',
                            fontSize: '12px',
                            lineHeight: '16px',
                            letterSpacing: '0.5px',
                            padding: '10px 0',
                        },
                        '& .rbc-month-header': {
                            display: 'none',
                        },
                        '& .rbc-date-cell': {
                            padding: '8px',
                            textAlign: 'left',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#3c4043',
                        },
                        '& .rbc-off-range': {
                            visibility: 'hidden',
                        },
                        '& .rbc-off-range-bg': {
                            background: '#ffffff',
                            zIndex: 5,
                        },
                        '& .rbc-row': {
                            position: 'relative',
                        },
                        '& .rbc-event': {
                            position: 'relative',
                            zIndex: 2,
                        },
                        '& .rbc-row-content': {
                            position: 'relative',
                        },
                    }}
                >
                    <Box
                        position='sticky'
                        top={0}
                        zIndex={3}
                        minWidth='900px'
                        display='flex'
                        sx={{
                            bgcolor: '#f3f4f6',
                            borderRadius: '4px 4px 0 0',
                            border: '1px solid #e5e7eb',
                        }}
                    >
                        {WEEKDAYS.map((day) => (
                            <Box
                                key={day}
                                sx={{
                                    flex: '1 0 0%',
                                    minWidth: 0,
                                    textAlign: 'center',
                                    color: '#364153',
                                    fontWeight: 400,
                                    textTransform: 'uppercase',
                                    fontSize: '12px',
                                    lineHeight: '16px',
                                    letterSpacing: '0.5px',
                                    padding: '10px 0',
                                    '&:not(:first-of-type)': {
                                        borderLeft: '1px solid #e5e7eb',
                                    },
                                }}
                            >
                                {day}
                            </Box>
                        ))}
                    </Box>
                    {months.map((date, index) => {
                        return (
                            <Box
                                key={`calendar-${index}`}
                                minWidth='900px'
                                data-month={moment(date).format('YYYY-MM')}
                            >
                                <Box
                                    sx={{
                                        bgcolor: '#f3f4f6',
                                        border: '1px solid #e5e7eb',
                                        borderTop: 'none',
                                        px: 1.5,
                                        py: 0.75,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#364153',
                                        letterSpacing: '0.5px',
                                        textAlign: 'center',
                                    }}
                                >
                                    {moment(date).format('MMMM YYYY')}
                                </Box>
                                <Box height={`${getMonthHeight(date)}px`}>
                                    <Calendar<EngineCalendarEvent, object>
                                        localizer={dateLocalizer}
                                        events={calendarEvents}
                                        date={date}
                                        view={Views.MONTH}
                                        views={[Views.MONTH]}
                                        toolbar={false}
                                        showMultiDayTimes={true}
                                        style={{ zIndex: 1, position: 'relative' }}
                                        components={calendarComponents}
                                        onNavigate={() => undefined}
                                        onView={() => undefined}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </DayMarkerContext.Provider>
        </DisciplineColorContext.Provider>
    );
};
